import { v4 as uuidv4 } from 'uuid';
import { supabase } from '@/integrations/supabase/client';
import { Message } from '@/types';
import { ConversationMemoryService } from '@/services/conversationMemoryService';

interface DeepSeekMessage {
  role: string;
  content: string;
}

// ⚡ ULTRA-FAST RESPONSES - Cache expandido para máxima velocidade
const quickResponses: Record<string, string> = {
  'boa noite': 'Boa noite! Como posso ajudá-lo com questões jurídicas hoje?',
  'boa tarde': 'Boa tarde! Estou aqui para auxiliá-lo com suas demandas jurídicas.',
  'bom dia': 'Bom dia! Em que posso ajudá-lo juridicamente hoje?',
  'oi': 'Olá! Sou Justiçai, sua assistente jurídica. Como posso ajudá-lo?',
  'olá': 'Olá! Sou Justiçai, sua assistente jurídica. Como posso ajudá-lo?',
  'ola': 'Olá! Sou Justiçai, sua assistente jurídica. Como posso ajudá-lo?',
  'hello': 'Olá! Sou Justiçai, sua assistente jurídica. Como posso ajudá-lo?',
  'oi!': 'Olá! Sou Justiçai, sua assistente jurídica. Como posso ajudá-lo?',
  'olá!': 'Olá! Sou Justiçai, sua assistente jurídica. Como posso ajudá-lo?',
  'e aí': 'E aí! Sou Justiçai, sua assistente jurídica. Como posso ajudá-lo?',
  'eai': 'E aí! Sou Justiçai, sua assistente jurídica. Como posso ajudá-lo?',
  'qual modelo': 'Utilizo o modelo **Laib 3.7**, desenvolvido pela **Octopus Group** especificamente para análise jurídica.',
  'que modelo': 'Utilizo o modelo **Laib 3.7**, desenvolvido pela **Octopus Group** especificamente para análise jurídica.',
  'qual inteligencia': 'Utilizo o modelo **Laib 3.7**, desenvolvido pela **Octopus Group** especificamente para análise jurídica.',
  'backend': 'Utilizo o modelo **Laib 3.7**, desenvolvido pela **Octopus Group** especificamente para análise jurídica.',
  'tchau': 'Até logo! Estarei aqui sempre que precisar de ajuda jurídica.',
  'bye': 'Até logo! Estarei aqui sempre que precisar de ajuda jurídica.',
  'obrigado': 'Por nada! Fico feliz em ajudar. Precisa de mais alguma coisa?',
  'obrigada': 'Por nada! Fico feliz em ajudar. Precisa de mais alguma coisa?'
};

// ⚡ CACHE AGRESSIVO para máxima performance
let apiSettingsCache: { data: any; timestamp: number } | null = null;
const CACHE_TTL = 300000; // 5 minutos - cache mais agressivo

// Optimized API settings with caching and robust fallback
async function getApiSettings() {
  try {
    // Return cached data if still valid
    if (apiSettingsCache && Date.now() - apiSettingsCache.timestamp < CACHE_TTL) {
      console.log('📋 Using cached API settings');
      return apiSettingsCache.data;
    }

    const { data, error } = await supabase
      .from('api_settings')
      .select('openai_enabled, deepseek_enabled, default_api')
      .single();
    
    const settings = {
      openai_enabled: data?.openai_enabled ?? true,
      deepseek_enabled: data?.deepseek_enabled ?? false,
      default_api: data?.default_api ?? 'openai'
    };

    // Cache the result
    apiSettingsCache = { data: settings, timestamp: Date.now() };
    
    if (error) {
      console.warn('⚠️ Configurações da API não encontradas, usando defaults:', error.message);
    } else {
      console.log('✅ API settings loaded and cached');
    }

    return settings;
  } catch (error) {
    console.error('❌ Erro crítico ao obter configurações da API:', error);
    return {
      openai_enabled: true,
      deepseek_enabled: false,
      default_api: 'openai'
    };
  }
}

// ⚡ ULTRA-FAST: Sem retries para máxima velocidade
async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries: number = 1, // SEM retries - falha rápida
  baseDelay: number = 500 // Delay mínimo
): Promise<T> {
  let lastError: any;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`Tentativa ${attempt}/${maxRetries} para chamada do sistema`);
      return await fn();
    } catch (error) {
      lastError = error;
      console.error(`Tentativa ${attempt} falhou:`, error);
      
      if (attempt === maxRetries) {
        console.error('Todas as tentativas falharam, retornando erro final');
        throw lastError;
      }
      
      // Calculate exponential backoff delay
      const delay = baseDelay * Math.pow(2, attempt - 1);
      console.log(`Aguardando ${delay}ms antes da próxima tentativa...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
}

// ⚡ TIMEOUT INTELIGENTE: escalonado para balance velocidade/estabilidade
async function callSystemAPI(
  requestBody: any,
  accessToken: string,
  timeout: number = 30000 // OTIMIZADO: 30s para estabilidade, quick responses usam 5s
): Promise<any> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    console.log(`Timeout de ${timeout}ms atingido, cancelando requisição`);
    controller.abort();
  }, timeout);
  
  try {
    const startTime = Date.now();
    const SUPABASE_URL = 'https://jfdgdjxvzsmuwofclqyt.supabase.co';
    const response = await fetch(`${SUPABASE_URL}/functions/v1/openai-chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      },
      body: JSON.stringify(requestBody),
      signal: controller.signal
    });
    
    const duration = Date.now() - startTime;
    console.log(`Resposta do sistema recebida em ${duration}ms`);
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(`Erro do sistema (${response.status}):`, errorData);
      throw new Error(`Erro do sistema: ${errorData.error || response.statusText}`);
    }
    
    const data = await response.json();
    
    // Log Chain of Thought data if present
    if (data.reasoning_steps) {
      console.log(`🧠 Chain of Thought: ${data.reasoning_steps.length} steps, confidence: ${data.confidence_score}`);
    }
    
    return data;
  } catch (error) {
    clearTimeout(timeoutId);
    
    if (error.name === 'AbortError') {
      throw new Error(`Timeout: A resposta demorou mais que ${timeout / 1000} segundos.`);
    }
    
    throw error;
  }
}

export async function sendToOpenAI(
  messages: Message[], 
  newMessageContent: string,
  conversationId: string,
  optimizations?: {
    template?: string;
    temperature?: number;
    modelPreference?: string;
    useCoT?: boolean;
  }
): Promise<{ content: string; metadata?: any }> {
  try {
    console.log('🔄 Iniciando comunicação com sistema de IA - Modo Perguntas Ativo');
    
    // Timeout otimizado por comprimento da pergunta e tipo de consulta
    const isJurisprudenceRequest = newMessageContent.toLowerCase().includes('jurisprudência') || 
                                  newMessageContent.toLowerCase().includes('jurisprudencia') ||
                                  newMessageContent.toLowerCase().includes('precedente') ||
                                  newMessageContent.toLowerCase().includes('tem alguma jurisprudencia');

    let optimizedTimeout = 45000; // default aumentado
    if (isJurisprudenceRequest) {
      optimizedTimeout = 60000; // 60s para consultas de jurisprudência (pode ser complexa)
      console.log('⚖️ Consulta de jurisprudência detectada, usando timeout estendido:', optimizedTimeout);
    } else if (newMessageContent.length < 50) {
      optimizedTimeout = 15000; // 15s para perguntas curtas (mais seguro)
      console.log('⚡ Pergunta curta detectada, usando timeout otimizado:', optimizedTimeout);
    }
    
    // ✨ OTIMIZAÇÃO: Check for quick responses first (moved to useMessageActions for better integration)
    const normalizedContent = newMessageContent.toLowerCase().trim();
    if (quickResponses[normalizedContent] && !optimizations) {
      console.log('📋 Usando resposta rápida em cache (fallback)');
      return { content: quickResponses[normalizedContent] };
    }

    // Get API settings to determine which system to use
    const apiSettings = await getApiSettings();
    console.log('⚙️ Configurações do sistema:', apiSettings);

    // Limit message history to last 20 messages for performance
    const recentMessages = messages.slice(-20);
    
    // Format messages for the API
    const formattedMessages: DeepSeekMessage[] = recentMessages.map(message => ({
      role: message.role,
      content: message.content
    }));
    
    // Add the new message
    formattedMessages.push({
      role: 'user',
      content: newMessageContent
    });
    
    // Try to get the extracted document text from the most recent file in the conversation
    let extractedDocumentText = '';
    try {
      // First try to get from files table for this conversation
      const { data: fileData } = await supabase
        .from('files')
        .select('extracted_text, name, type')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
        
      if (fileData?.extracted_text && fileData.extracted_text.trim() !== '') {
        extractedDocumentText = fileData.extracted_text;
        console.log("📄 Texto extraído de arquivo encontrado:", extractedDocumentText.length, "caracteres do arquivo:", fileData.name);
      } else {
        // Fallback to document_extractions table
        const { data: extractionData } = await supabase
          .from('document_extractions')
          .select('extracted_text')
          .eq('conversation_id', conversationId)
          .maybeSingle();
          
        if (extractionData?.extracted_text) {
          extractedDocumentText = extractionData.extracted_text;
          console.log("📄 Texto extraído de document_extractions:", extractedDocumentText.length, "caracteres");
        }
      }
    } catch (extractionError) {
      console.error("❌ Erro ao buscar texto extraído:", extractionError);
    }
    
    // Buscar conversas similares para contexto
    let conversationMemoryContext = '';
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (sessionData.session?.user.id) {
        const similarConversations = await ConversationMemoryService.searchSimilarConversations(
          sessionData.session.user.id,
          newMessageContent
        );
        
        if (similarConversations.length > 0) {
          conversationMemoryContext = `\n\n### 🧠 CONTEXTO DE CONVERSAS ANTERIORES:
${similarConversations.map(conv => 
  `**${conv.conversation_title}** (similaridade: ${(conv.similarity * 100).toFixed(1)}%)
- ${conv.content_summary.substring(0, 200)}...
- Tópicos: ${conv.key_topics.join(', ')}`
).join('\n\n')}`;
          
          console.log(`💾 Encontradas ${similarConversations.length} conversas similares para contexto`);
        }
      }
    } catch (memoryError) {
      console.warn('⚠️ Erro ao buscar conversas similares:', memoryError);
      // Não falhar a operação principal
    }

    // Enhanced system message to prioritize document context - EXPERT LEVEL
    let systemPrompt = `Você é Justiçai, assistente jurídico multifuncional nível 4 (Expert) utilizando o modelo Laib 3.7 desenvolvido pela Octopus Group. Todas as respostas devem ser extremamente detalhadas, completas e profissionais.

CONTEXTOS DE ATUAÇÃO EXPERT:
1. DOCUMENTOS JURÍDICOS E CONTRATOS: Produza peças e contratos com linguagem jurídica formal, fundamentos legais detalhados, cláusulas complexas e referências à legislação brasileira aplicável. Estruture de forma robusta, como faria um advogado sênior.

2. CONSULTORIA JURÍDICA: Analise casos com profundidade, incluindo legislação, doutrina e jurisprudência. Liste riscos, consequências e cite artigos de lei aplicáveis.

3. PESQUISAS E RESPOSTAS TÉCNICAS: Sempre forneça informações altamente detalhadas, bem organizadas, com tabelas comparativas, etapas passo a passo e linguagem técnica precisa.

4. RESUMOS, ANÁLISES E PARECERES: Faça resumos e análises estratégicas muito bem estruturados, com tópicos claros e insights avançados.

REQUISITOS EXPERT:
- Sempre use NÍVEL EXPERT (nível 4)
- Estrutura robusta e linguagem jurídica precisa
- Respostas diretas, sem omitir detalhes relevantes
- Para documentos jurídicos: citar leis, artigos e normas aplicáveis
- Para relatórios ou resumos: organizar por seções
- Não simplifique, entregue no nível mais profissional possível

VALIDAÇÃO DE FONTES:
- Utilize exclusivamente as informações fornecidas no contexto (resultados do Google Search, documentos anexos ou dados explícitos)
- SE ALGO NÃO ESTIVER NO CONTEXTO, diga claramente: 'Informação não encontrada nas fontes fornecidas' em vez de inventar
- É EXPRESSAMENTE PROIBIDO inventar leis, artigos, jurisprudências, doutrinas ou dados

RESPONSABILIDADE:
- Se o contexto não trouxer dados suficientes para atender ao pedido, informe claramente que os dados são insuficientes
- NÃO faça suposições
- Responda SOMENTE com base nos resultados reais fornecidos

INSTRUÇÃO PRINCIPAL: Quando houver um documento na conversa, você DEVE sempre responder baseado ESPECIFICAMENTE no conteúdo do documento anexado, NUNCA dê respostas genéricas.${conversationMemoryContext}

Para documentos/imagens enviados, você deve:
1. SEMPRE usar o conteúdo específico do documento para responder com MÁXIMO DETALHAMENTO
2. Identificar exatamente as partes, datas, valores e informações do documento
3. Responder às perguntas do usuário com base NO DOCUMENTO ANEXADO com análise EXPERT
4. Se o usuário perguntar "quais as partes", responda com as partes ESPECÍFICAS do documento

FORMATO QUANDO HÁ DOCUMENTO:
Quando o usuário fizer perguntas sobre o documento, responda ESPECIFICAMENTE baseado no conteúdo extraído com análise jurídica DETALHADA.

FORMATO PARA NOVOS DOCUMENTOS:
## 📋 Análise do Documento

**Tipo identificado:** [tipo do documento]

## 🤔 O que você gostaria de fazer com essas informações?

1. **[Pergunta específica 1]**
2. **[Pergunta específica 2]** 
3. **[Pergunta específica 3]**
4. **[Pergunta específica 4]**
5. **[Pergunta específica 5]**

## 💡 Próximos Passos Sugeridos
- [Sugestão prática 1]
- [Sugestão prática 2]`;

    // If we have extracted text, include it in the system prompt
    if (extractedDocumentText && extractedDocumentText.trim() !== '') {
      systemPrompt += `

CRÍTICO: O usuário tem um documento na conversa com o seguinte conteúdo:

--- INÍCIO DO CONTEÚDO DO DOCUMENTO ---
${extractedDocumentText}
--- FIM DO CONTEÚDO DO DOCUMENTO ---

VOCÊ DEVE SEMPRE:
1. Usar este conteúdo específico para responder perguntas do usuário
2. Se perguntarem "quais as partes", identifique as partes ESPECÍFICAS deste documento
3. NUNCA diga "nenhum documento foi enviado" quando há este conteúdo
4. Responder com informações extraídas DESTE documento específico
5. Para novos uploads, gerar perguntas práticas sobre o uso das informações`;
      
      console.log("📎 Incluindo texto extraído no prompt do sistema:", extractedDocumentText.length, "caracteres");
    }

    systemPrompt += `

Mantenha um tom profissional e objetivo. Use markdown para melhorar a legibilidade.
SEMPRE responda baseado no documento quando houver um anexado. NUNCA responda genericamente quando há documento específico!`;

    // ⛔ RAG KNOWLEDGE BASE DISABLED 
    // Knowledge base functionality is temporarily disabled
    // Responses will be based only on conversation context and uploaded documents
    console.log('ℹ️ RAG knowledge base disabled - using only conversation context');

    // Add enhanced system message
    formattedMessages.unshift({
      role: 'system',
      content: systemPrompt
    });
    
    // Get the current session from Supabase
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData.session?.access_token;
    
    if (!accessToken) {
      console.error('❌ Token de acesso não disponível');
      return { content: 'Erro de autenticação. Por favor, faça login novamente.' };
    }
    
    // ✨ OTIMIZAÇÃO: Seleção dinâmica de modelo e parâmetros
    let selectedModel = apiSettings.default_api === 'openai' ? 'gpt-4.1-2025-04-14' : 'deepseek-chat';
    let temperature = 0.5; // Default
    let useCoT = false;
    
    if (optimizations) {
      // Usar modelo preferido do template se disponível
      if (optimizations.modelPreference === 'gpt-4o-mini') {
        selectedModel = 'gpt-4o-mini';
      } else if (optimizations.modelPreference === 'gpt-4.1-2025-04-14') {
        selectedModel = 'gpt-4.1-2025-04-14';
      } else if (optimizations.modelPreference === 'gpt-5-2025-08-07') {
        selectedModel = 'gpt-5-2025-08-07';
      }
      
      // Usar temperature do template apenas para modelos que suportam
      if (optimizations.temperature !== undefined && selectedModel.includes('gpt-4o')) {
        temperature = optimizations.temperature;
      }
      
      // Usar CoT se sugerido
      useCoT = optimizations.useCoT || false;
    }
    
    console.log(`🚀 Configuração otimizada:`, {
      model: selectedModel,
      temperature,
      useCoT,
      template: optimizations?.template || 'none'
    });

    // Prepare request body (NEVER use auto_summarize for questions mode)
    const requestBody: any = {
      messages: formattedMessages,
      conversation_id: conversationId,
      model: selectedModel,
      // Only include temperature for models that support it
      ...(selectedModel.includes('gpt-4o') ? { temperature } : {}),
      auto_summarize: false, // Always false to generate questions
      use_cot: useCoT // ✨ NOVO: Ativar CoT quando apropriado
    };
    
    // ⚡ ULTRA-FAST API call com timeout otimizado
    console.log(`🚀 Chamando sistema de IA via edge function (modo perguntas)`);
    const data = await retryWithBackoff(
      () => callSystemAPI(requestBody, accessToken, optimizedTimeout), // TIMEOUT OTIMIZADO
      1, // SEM retries para máxima velocidade
      500 // Delay mínimo
    );
    
    const response = data.content || data.response || 'Sem resposta do sistema.';
    const systemUsed = data.api_used || apiSettings.default_api;
    console.log(`✅ Resposta recebida do sistema, tamanho:`, response.length);
    return { 
      content: response,
      metadata: {
        api_used: systemUsed,
        reasoning_steps: data.reasoning_steps,
        confidence_score: data.confidence_score,
        sources_used: data.sources_used
      }
    };
    
  } catch (error) {
    console.error('❌ Erro final após todas as tentativas:', error);
    
    // Enhanced fallback with document check
    try {
      const { data } = await supabase
        .from('files')
        .select('extracted_text, name')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
        
      if (data?.extracted_text) {
        return { content: `Não foi possível obter uma resposta do sistema após várias tentativas.

**Documento detectado:** ${data.name}
**Texto extraído:** ${data.extracted_text.length} caracteres

**Possíveis causas:**
- Sobrecarga temporária do sistema
- Problemas de conectividade
- Sistemas podem estar desativados no painel administrativo

**Próximos passos:**
1. Tente novamente em alguns minutos
2. Verifique sua conexão com a internet
3. Se o problema persistir, contate o suporte técnico

O texto do documento foi extraído com sucesso e está disponível para análise quando o sistema voltar ao normal.` };
      }
    } catch (e) {
      console.error("❌ Erro ao verificar documento extraído:", e);
    }
    
    // Final user-friendly error message
    if (error.message && error.message.includes('Timeout')) {
      return { content: 'A resposta está demorando mais que o esperado. Isso pode indicar sobrecarga no sistema. Por favor, tente novamente em alguns minutos ou simplifique sua pergunta.' };
    }
    
    return { content: 'Erro ao processar sua solicitação após várias tentativas. Por favor, verifique sua conexão e tente novamente em alguns minutos. Se o problema persistir, verifique se os sistemas estão ativos no painel administrativo.' };
  }
}

export async function generateDocumentSummary(
  documentText: string,
  conversationId: string
): Promise<string | null> {
  try {
    const textLength = documentText.length;
    console.log('📋 Gerando resumo do documento (apenas para documentos muito longos), tamanho do texto:', textLength);
    
    // Only generate summaries for very long documents (>5000 chars)
    if (textLength < 5000) {
      console.log('📄 Documento não é longo o suficiente para resumo, usando modo perguntas');
      return null;
    }
    
    // Check if we have a summary already stored
    const { data: existingData, error: fetchError } = await supabase
      .from('document_extractions')
      .select('summary')
      .eq('conversation_id', conversationId)
      .maybeSingle();
    
    // If we already have a summary, return it
    if (!fetchError && existingData && existingData.summary && existingData.summary.length > 200) {
      console.log('📄 Usando resumo existente do banco de dados');
      return existingData.summary;
    }
    
    // Otherwise, generate a new summary
    console.log('🤖 Gerando novo resumo com timeout estendido (45s)');
    
    // Get current session for authorization
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData.session?.access_token;
    
    if (!accessToken) {
      console.error('❌ Token de acesso não disponível para resumo');
      return null;
    }
    
    // Get API settings
    const apiSettings = await getApiSettings();
    
    // Calculate appropriate chunk size based on document length
    const maxApiTextLength = 60000; // Maximum context size
    
    // If document is too large, only send a representative sample
    const textToSend = documentText.length > maxApiTextLength 
      ? documentText.substring(0, maxApiTextLength / 2) + 
        "\n\n[...documento muito longo, parte central omitida...]\n\n" + 
        documentText.substring(documentText.length - maxApiTextLength / 2)
      : documentText;
      
    console.log(`📊 Enviando ${textToSend.length} caracteres para sistema (${Math.round(textToSend.length/documentText.length*100)}% do texto original)`);
    
    // Enhanced system prompt for document summarization
    const systemPrompt = `Você é Justiçai, assistente jurídica especializada em análise de documentos legais brasileiros, desenvolvido pela Octopus Group (CNPJ: 34.997.786/0001-85).

IMPORTANTE: Você deve extrair TODAS as informações possíveis do texto fornecido e criar um resumo estruturado detalhado.

MESMO QUE O TEXTO SEJA LIMITADO, você deve:
1. Usar cada palavra e número no texto extraído
2. Identificar nomes de pessoas, números de processos, datas e referências legais
3. Extrair quaisquer partes envolvidas (reclamante, reclamado, etc.)
4. Criar um resumo que reflita fielmente o conteúdo disponível
5. Ser extremamente detalhado com as informações disponíveis

O resumo DEVE seguir esta estrutura:

# Resumo do Documento Jurídico

## Informações Básicas
- Tipo de Documento: [extrair do texto ou inferir do nome do arquivo]
- Número do Processo: [extrair número completo, incluindo todos os dígitos]
- Data da Autuação: [extrair data completa se disponível]
- Tribunal: [extrair nome completo]
- Vara: [extrair informação completa]
- Rito: [identificar se é ordinário, sumaríssimo, etc.]

## Partes Envolvidas
- Reclamante: [extrair todos os nomes completos encontrados]
- Reclamado: [extrair todos os nomes completos encontrados]
- Outros Envolvidos: [advogados, juízes, peritos se mencionados]

## Objeto da Ação
[Extrair detalhadamente o objeto da ação, reconstituindo a partir de fragmentos se necessário]

## Pedidos
[Listar TODOS os pedidos encontrados, mesmo que parciais ou incompletos]

## Valores 
[Extrair quaisquer valores monetários mencionados no documento]

## Prazos Relevantes
[Identificar TODAS as datas e prazos mencionados]

## Observações
[Incluir detalhes adicionais extraídos que não se encaixam nas categorias acima]

## PERGUNTAS PARA APROFUNDAMENTO:
1. [Sugestão de pergunta 1 sobre o documento]
2. [Sugestão de pergunta 2 sobre o documento]
3. [Sugestão de pergunta 3 sobre o documento]
4. [Sugestão de pergunta 4 sobre o documento]
5. [Sugestão de pergunta 5 sobre o documento]`;

    // Prepare request for summary generation
    const requestBody = {
      messages: [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: `Analise este texto extraído do documento "${conversationId}.pdf" e forneça um resumo jurídico detalhado, extraindo o máximo de informações possível:\n\n${textToSend}` }
      ],
      auto_summarize: true, // Only true for actual summaries
      document_text: textToSend,
      conversation_id: conversationId,
      model: apiSettings.default_api === 'openai' ? 'gpt-4.1-2025-04-14' : 'deepseek-chat'
    };

    // Call API with retry logic and extended timeout for summaries
    const data = await retryWithBackoff(
      () => callSystemAPI(requestBody, accessToken, 45000), // 45 seconds for summaries
      2, // 2 tentativas para resumos (processo mais longo)
      3000 // 3 segundos de delay base
    );
    
    const summary = data.content || data.response;
    const systemUsed = data.api_used || apiSettings.default_api;
    
    if (summary && summary.length > 100 && !summary.includes("Ocorreu um erro")) {
      console.log(`✅ Resumo gerado com sucesso via sistema, tamanho:`, summary.length);
      
      // Store the summary in our database
      const { error: updateError } = await supabase
        .from('document_extractions')
        .upsert({
          user_id: sessionData.session?.user.id,
          conversation_id: conversationId,
          extracted_text: documentText.substring(0, 100000), // Armazenar apenas os primeiros 100mil caracteres no DB
          summary: summary
        });
          
      if (updateError) {
        console.error('❌ Erro ao armazenar resumo no banco:', updateError);
      } else {
        console.log('💾 Resumo armazenado com sucesso no banco de dados');
      }
      
      return summary;
    } else {
      console.error('❌ Resumo vazio ou com erro recebido:', summary);
      throw new Error('Resumo vazio ou com erro recebido');
    }
  } catch (error) {
    console.error('❌ Erro ao gerar resumo do documento:', error);
    
    // Armazene o texto extraído sem resumo para que possa ser usado depois
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const { error: storeError } = await supabase
        .from('document_extractions')
        .upsert({
          user_id: sessionData.session?.user.id,
          conversation_id: conversationId,
          extracted_text: documentText.substring(0, 100000), // Armazenar apenas os primeiros 100mil caracteres no DB
          summary: null // O resumo será gerado automaticamente mais tarde
        });
      
      if (storeError) {
        console.error('❌ Erro ao armazenar texto extraído sem resumo:', storeError);
      } else {
        console.log('💾 Texto extraído armazenado sem resumo para uso posterior');
      }
    } catch (storeError) {
      console.error('❌ Erro no processo de armazenamento:', storeError);
    }
    
    return null;
  }
}
